import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "react-toastify";
import EditOpponentModal from "./EditOpponentModal";
import apiService from "../../services/api.service";
import logger from "../../utils/logger";

const ViewOpponentModal = ({
  isOpen,
  onClose,
  opponentId,
  onOpponentUpdated,
}) => {
  const [opponent, setOpponent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (opponentId && isOpen) {
      fetchOpponentDetails();
    }
  }, [opponentId, isOpen]);

  const fetchOpponentDetails = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get(`/opponents/${opponentId}`);

      if (response.success) {
        setOpponent(response.data);
      } else {
        toast.error(response.message || "حدث خطأ أثناء جلب بيانات الخصم");
      }
    } catch (error) {
      logger.error("Error fetching opponent details:", error);
      toast.error(
        error.response?.data?.message || "حدث خطأ أثناء جلب بيانات الخصم"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpponentUpdated = (updatedOpponent) => {
    setOpponent(updatedOpponent);
    if (onOpponentUpdated) {
      onOpponentUpdated(updatedOpponent);
    }
    setShowEditModal(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800">نشط</Badge>;
      case "settled":
        return <Badge className="bg-blue-100 text-blue-800">تم تسوية</Badge>;
      case "inactive":
        return <Badge className="bg-gray-100 text-gray-800">غير نشط</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const getOpponentTypeBadge = (type) => {
    switch (type) {
      case "مدعي":
        return <Badge variant="outline">مدعى</Badge>;
      case "مدعى عليه":
        return <Badge variant="outline">مدعى عليه</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>تفاصيل الخصم</CardTitle>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(true)}
                disabled={isLoading}
              >
                تعديل
              </Button>
              <Button variant="ghost" onClick={onClose}>
                ×
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : opponent ? (
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="details">معلومات الخصم</TabsTrigger>
                  <TabsTrigger value="lawyer">معلومات المحامي</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">{opponent.name}</h3>
                    <div className="flex space-x-2">
                      {getOpponentTypeBadge(opponent.opponent_type)}
                      {getStatusBadge(opponent.status)}
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h4 className="font-medium">معلومات الاتصال</h4>
                      <div className="space-y-1">
                        {opponent.phone && (
                          <p className="text-sm">
                            <span className="font-medium">الهاتف:</span>{" "}
                            {opponent.phone}
                          </p>
                        )}
                        {opponent.email && (
                          <p className="text-sm">
                            <span className="font-medium">
                              البريد الإلكتروني:
                            </span>{" "}
                            {opponent.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <h4 className="font-medium">العنوان</h4>
                      <div className="space-y-1">
                        {opponent.address && (
                          <p className="text-sm">{opponent.address}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {opponent.case_number && (
                    <div className="space-y-2">
                      <h4 className="font-medium">معلومات القضية</h4>
                      <div className="space-y-1">
                        <p className="text-sm">
                          <span className="font-medium">رقم القضية:</span>{" "}
                          {opponent.case_number}
                        </p>
                        {opponent.case_title && (
                          <p className="text-sm">
                            <span className="font-medium">عنوان القضية:</span>{" "}
                            {opponent.case_title}
                          </p>
                        )}
                        {opponent.client_name && (
                          <p className="text-sm">
                            <span className="font-medium">العميل:</span>{" "}
                            {opponent.client_name}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {opponent.notes && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <h4 className="font-medium">ملاحظات</h4>
                        <p className="text-sm">{opponent.notes}</p>
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="lawyer" className="space-y-4">
                  {opponent.lawyer_name ||
                  opponent.lawyer_phone ||
                  opponent.lawyer_office ? (
                    <>
                      <h3 className="text-lg font-medium">
                        معلومات المحامي/الوكيل
                      </h3>

                      <Separator />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <h4 className="font-medium">معلومات المحامي</h4>
                          <div className="space-y-1">
                            {opponent.lawyer_name && (
                              <p className="text-sm">
                                <span className="font-medium">الاسم:</span>{" "}
                                {opponent.lawyer_name}
                              </p>
                            )}
                            {opponent.lawyer_phone && (
                              <p className="text-sm">
                                <span className="font-medium">الهاتف:</span>{" "}
                                {opponent.lawyer_phone}
                              </p>
                            )}
                          </div>
                        </div>

                        {opponent.lawyer_office && (
                          <div className="space-y-2">
                            <h4 className="font-medium">مكتب المحاماة</h4>
                            <p className="text-sm">{opponent.lawyer_office}</p>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">
                        لا توجد معلومات محامي/وكيل مسجلة
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">لم يتم العثور على بيانات الخصم</p>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowEditModal(true)}
                disabled={isLoading || !opponent}
              >
                تعديل
              </Button>
              <Button variant="outline" onClick={onClose}>
                إغلاق
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {showEditModal && (
        <EditOpponentModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          opponentId={opponentId}
          onOpponentUpdated={handleOpponentUpdated}
        />
      )}
    </>
  );
};

export default ViewOpponentModal;
